import { useEffect } from 'react';
import { useMutation as apolloUseMutation } from 'react-apollo';
import { DocumentNode } from 'graphql';
import { map } from 'ramda';

import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import { getGraphQLError } from './getGraphQLError';
import { ModalError } from 'components/Modal/ModalError';

const defaultErrorMessage = {
  displayMessage: 'Something went wrong',
  message: 'Not enough data to display at this time.',
};

type ErrorMapping = {
  key: string;
  newError: ModalError;
};

const errorsMapping = (errorsMappings: ErrorMapping[]) =>
  map((error: ModalError) => {
    let mappedError = error;

    errorsMappings.forEach((errorMapping: ErrorMapping) => {
      if (error.message.includes(errorMapping.key)) {
        mappedError = errorMapping.newError;
      }
    });

    return mappedError;
  });

/**
 *  React hooks that wraps the useMutation hook from react-apollo.
 *  It's integrated with the toastMapping helper to provide sucess and error messages automatically.
 */
export const useMutation = (
  query: DocumentNode,
  variables?: Record<string, unknown>,
  successMessage?: string,
  errorMappings?: ErrorMapping[],
  onSuccess?: () => void,
) => {
  const [runMutation, { data, loading, error }] = apolloUseMutation(query, {
    variables,
  });

  useEffect(() => {
    if (data && successMessage && !loading) {
      toastMapping(
        {},
        'success',
      )([
        {
          message: successMessage,
          displayMessage: 'Success',
        },
      ]);
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      let errors = getGraphQLError(defaultErrorMessage, false, error);

      if (errorMappings) {
        errors = errorsMapping(errorMappings)(errors);
      }

      toastMapping({}, 'error')(errors);
    }
  }, [error]);

  return { runMutation, loading, data, error };
};
